/**
 * Auth Context with RBAC Support
 * Provides authentication state, role-based permissions, and role preview for admins
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authService, tenantService } from '../services/api';
import { 
  ROLES, 
  ROLE_PERMISSIONS, 
  hasPermission, 
  hasAnyPermission, 
  canAccessRoute,
  getNavigationForRole,
  getDashboardForRole,
  getHomeRouteForRole,
  canPerformAction,
  ROLE_DISPLAY_NAMES,
} from '../config/rbac';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [currentOutlet, setCurrentOutlet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Role preview state (for admin role selector)
  const [previewRole, setPreviewRole] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedUser = authService.getCurrentUser();
        if (storedUser && authService.isAuthenticated()) {
          // Verify token is still valid
          const freshUser = await authService.getMe();
          setUser(freshUser);
          
          // Load outlets
          if (freshUser.tenant_id) {
            const outletList = await tenantService.getOutlets(freshUser.tenant_id);
            setOutlets(outletList);
            
            // Set default outlet (first one or from localStorage)
            const savedOutletId = localStorage.getItem('isf_current_outlet');
            const defaultOutlet = savedOutletId 
              ? outletList.find(o => o.id === savedOutletId) || outletList[0]
              : outletList[0];
            setCurrentOutlet(defaultOutlet);
          }
        }
      } catch (err) {
        console.error('Auth init error:', err);
        authService.clearAuth();
      } finally {
        setLoading(false);
      }
    };
    
    initAuth();
  }, []);
  
  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      // Initial login to get tokens
      await authService.login(email, password);
      
      // Immediately fetch full user data including role
      const userData = await authService.getMe();
      setUser(userData);
      
      // Clear any preview mode on new login
      setPreviewRole(null);
      setIsPreviewMode(false);
      
      // Set loading to false immediately after user is set - outlets load in background
      setLoading(false);
      
      // Load outlets in background (don't block UI)
      if (userData.tenant_id) {
        tenantService.getOutlets(userData.tenant_id)
          .then(outletList => {
            setOutlets(outletList);
            if (outletList.length > 0) {
              setCurrentOutlet(outletList[0]);
              localStorage.setItem('isf_current_outlet', outletList[0].id);
            }
          })
          .catch(err => console.warn('Failed to load outlets:', err));
      }
      
      return userData;
    } catch (err) {
      setLoading(false);
      const message = err.response?.data?.detail || err.message || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  }, []);
  
  // Login with OAuth token (for Google Sign-In callback)
  const loginWithToken = useCallback(async (token) => {
    setLoading(true);
    setError(null);
    
    try {
      // Store the token directly
      authService.setTokens(token, token);
      
      // Fetch user data
      const userData = await authService.getMe();
      setUser(userData);
      
      // Clear any preview mode
      setPreviewRole(null);
      setIsPreviewMode(false);
      
      // Set loading to false immediately
      setLoading(false);
      
      // Load outlets in background
      if (userData.tenant_id) {
        tenantService.getOutlets(userData.tenant_id)
          .then(outletList => {
            setOutlets(outletList);
            if (outletList.length > 0) {
              setCurrentOutlet(outletList[0]);
              localStorage.setItem('isf_current_outlet', outletList[0].id);
            }
          })
          .catch(err => console.warn('Failed to load outlets:', err));
      }
      
      return userData;
    } catch (err) {
      setLoading(false);
      authService.clearAuth();
      const message = err.response?.data?.detail || err.message || 'Token login failed';
      setError(message);
      throw new Error(message);
    }
  }, []);
  
  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setTenant(null);
      setOutlets([]);
      setCurrentOutlet(null);
      setPreviewRole(null);
      setIsPreviewMode(false);
      localStorage.removeItem('isf_current_outlet');
      setLoading(false);
    }
  }, []);
  
  const switchOutlet = useCallback((outlet) => {
    setCurrentOutlet(outlet);
    localStorage.setItem('isf_current_outlet', outlet.id);
  }, []);
  
  // ============================================
  // ROLE PREVIEW FUNCTIONS (Admin only)
  // ============================================
  
  /**
   * Enable role preview mode (only for admins)
   */
  const enableRolePreview = useCallback((role) => {
    // Only admins can preview roles
    const actualRole = user?.role;
    if (actualRole !== ROLES.CENTRAL_ADMIN && actualRole !== ROLES.BRAND_OWNER) {
      console.warn('Role preview is only available for admins');
      return false;
    }
    
    if (!Object.values(ROLES).includes(role)) {
      console.warn('Invalid role for preview:', role);
      return false;
    }
    
    setPreviewRole(role);
    setIsPreviewMode(true);
    return true;
  }, [user]);
  
  /**
   * Disable role preview mode
   */
  const disableRolePreview = useCallback(() => {
    setPreviewRole(null);
    setIsPreviewMode(false);
  }, []);
  
  /**
   * Check if current user can use role preview
   */
  const canUseRolePreview = useMemo(() => {
    const actualRole = user?.role;
    return actualRole === ROLES.CENTRAL_ADMIN || actualRole === ROLES.BRAND_OWNER;
  }, [user]);
  
  // ============================================
  // PERMISSION HELPERS
  // ============================================
  
  /**
   * Get the effective role (preview or actual)
   */
  const effectiveRole = useMemo(() => {
    if (isPreviewMode && previewRole) {
      return previewRole;
    }
    return user?.role || null;
  }, [isPreviewMode, previewRole, user]);
  
  /**
   * Get actual role (ignores preview)
   */
  const actualRole = useMemo(() => user?.role || null, [user]);
  
  /**
   * Check if user has a specific permission
   */
  const checkPermission = useCallback((permission) => {
    return hasPermission(effectiveRole, permission);
  }, [effectiveRole]);
  
  /**
   * Check if user has any of the specified permissions
   */
  const checkAnyPermission = useCallback((permissions) => {
    return hasAnyPermission(effectiveRole, permissions);
  }, [effectiveRole]);
  
  /**
   * Check if user can access a route
   */
  const checkRouteAccess = useCallback((path) => {
    return canAccessRoute(effectiveRole, path);
  }, [effectiveRole]);
  
  /**
   * Check if user can perform an action
   */
  const checkAction = useCallback((action) => {
    return canPerformAction(effectiveRole, action);
  }, [effectiveRole]);
  
  /**
   * Get navigation items for current role
   */
  const navigation = useMemo(() => {
    return getNavigationForRole(effectiveRole);
  }, [effectiveRole]);
  
  /**
   * Get dashboard config for current role
   */
  const dashboardConfig = useMemo(() => {
    return getDashboardForRole(effectiveRole);
  }, [effectiveRole]);
  
  /**
   * Get home route for current role
   */
  const homeRoute = useMemo(() => {
    return getHomeRouteForRole(effectiveRole);
  }, [effectiveRole]);
  
  /**
   * Get all permissions for current role
   */
  const permissions = useMemo(() => {
    return ROLE_PERMISSIONS[effectiveRole] || [];
  }, [effectiveRole]);
  
  /**
   * Get role display name
   */
  const roleDisplayName = useMemo(() => {
    return ROLE_DISPLAY_NAMES[effectiveRole] || effectiveRole;
  }, [effectiveRole]);
  
  const value = {
    // User state
    user,
    tenant,
    outlets,
    currentOutlet,
    loading,
    error,
    isAuthenticated: !!user,
    
    // Auth actions
    login,
    loginWithToken,
    logout,
    switchOutlet,
    
    // Role preview (Admin feature)
    isPreviewMode,
    previewRole,
    canUseRolePreview,
    enableRolePreview,
    disableRolePreview,
    
    // Role & Permissions
    effectiveRole,
    actualRole,
    roleDisplayName,
    permissions,
    checkPermission,
    checkAnyPermission,
    checkRouteAccess,
    checkAction,
    
    // Navigation
    navigation,
    dashboardConfig,
    homeRoute,
    
    // Legacy convenience getters (for backward compatibility)
    tenantId: user?.tenant_id,
    outletId: currentOutlet?.id,
    userRole: effectiveRole, // Use effective role for all permission checks
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export RBAC constants for convenience
export { ROLES, PERMISSIONS } from '../config/rbac';

export default AuthContext;
